'use client'

import { useQueryClient } from '@tanstack/react-query'
import { Globe, KeyboardIcon, Loader2, LogOut, Mail, ShieldCheck, Sparkles, UserRoundCog } from 'lucide-react'
import { memo, useCallback } from 'react'

import { clearSessionCookiesRedirect } from '@api/authMiddleware'
import { getGetCurrentSessionQueryKey, useGetCurrentSession } from '@api/orval/apiHooks'
import { DARK_THEMES, LIGHT_THEMES, SYSTEM_THEMES, themeIconProps, useTheme, type Theme } from '@app/_ThemeProvider'
import { Badge, Button, ScrollFadeContainer } from '@fp/ui-kit'
import { copyToClipboard } from '@lib/copyToClipboard'
import { dayjs, isValidTimezone } from '@lib/dayjs'
import { emitToast } from '@lib/emitToast'
import { iconProps } from '@lib/iconProps'
import { splitAndCapitalizeEachWord } from '@lib/textFormatters'
import { useAppDispatch } from '@store/hooks'
import { signOut } from '@store/modules/session/slice'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/DropDownMenu'
import { ToggleGroup, ToggleGroupItem } from '@ui/ToggleGroup'

const THEME_OPTIONS = [...LIGHT_THEMES, ...SYSTEM_THEMES, ...DARK_THEMES]

interface TopNavUserMenuProps {
  isAppHotkeysEnabled: boolean
  onToggleAppHotkeys: (e: React.MouseEvent<HTMLDivElement>) => void
}

export const TopNavUserMenu = memo(({ isAppHotkeysEnabled, onToggleAppHotkeys }: TopNavUserMenuProps) => {
  const { data: user } = useGetCurrentSession()
  const dispatch = useAppDispatch()
  const qc = useQueryClient()
  const { theme, isSparkle, setTheme, toggleSparkle } = useTheme()

  const handleSignOut = useCallback(async () => {
    qc.removeQueries({ queryKey: getGetCurrentSessionQueryKey() })

    await clearSessionCookiesRedirect()

    dispatch(signOut())
  }, [dispatch, qc])

  const handleCopyEmail = useCallback(async () => {
    if (!user?.email) return

    if (await copyToClipboard(user.email)) {
      emitToast({ message: 'Email copied', details: [{ message: user.email }], type: 'info' })
    } else {
      emitToast({ message: 'Copy failed', type: 'error' })
    }
  }, [user])

  const handleThemeToggle = useCallback(
    (name: Theme) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      setTheme(name)
    },
    [setTheme],
  )

  const handleSparkleToggle = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      toggleSparkle()
    },
    [toggleSparkle],
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={user ? 'ghostOutline' : 'ghost'}
          size='sm'
          className='w-30 overflow-hidden text-ellipsis whitespace-nowrap h-fit min-h-8'
          title={user?.email ?? undefined}
          aria-label={user ? `Account email ${user.email}` : 'Loading'}
        >
          {user?.email ? (
            <span className='flex truncate items-center gap-2'>
              <UserRoundCog {...themeIconProps} /> {user?.email?.split('@')[0] ?? user.email}
            </span>
          ) : (
            <Loader2 className='text-muted-foreground animate-spin' />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side='bottom' align='center' className='min-w-56 max-w-64'>
        <DropdownMenuItem
          onClick={handleCopyEmail}
          title={user?.email ? `Email: ${user.email}` : undefined}
          aria-label='Email'
          className='focus:bg-transparent cursor-pointer py-2'
        >
          <Mail {...themeIconProps} className='!size-4 text-muted-foreground shrink-0' />
          <ScrollFadeContainer horizontal className='hover:text-primary'>
            <div role='marquee' className='flex items-center hover:animate-marquee relative whitespace-nowrap'>
              {user?.email}
            </div>
          </ScrollFadeContainer>
        </DropdownMenuItem>

        {isValidTimezone(user?.timezone) && (
          <DropdownMenuItem
            title={`Timezone: ${user.timezone}`}
            aria-label='Timezone'
            className='focus:bg-transparent py-2'
          >
            <Globe {...themeIconProps} className='!size-4 text-muted-foreground shrink-0' />
            <ScrollFadeContainer horizontal>
              <div role='marquee' className='flex items-center hover:animate-marquee relative whitespace-nowrap'>
                {(user.timezone.split('/').pop() ?? user.timezone).replace(/_/g, ' ')} (UTC
                {dayjs().tz(user.timezone).format('Z')})
              </div>
            </ScrollFadeContainer>
          </DropdownMenuItem>
        )}

        {user?.role && (
          <DropdownMenuItem title={`Role: ${user.role}`} aria-label='Role' className='focus:bg-transparent py-2'>
            <ShieldCheck {...themeIconProps} className='!size-4 text-muted-foreground shrink-0' />
            <Badge variant='primary' hasDot>
              {splitAndCapitalizeEachWord(user.role)}
            </Badge>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem className='focus:bg-transparent'>
          <ToggleGroup type='multiple' variant='outline' value={[theme]}>
            {THEME_OPTIONS.map(({ icon, name }) => (
              <ToggleGroupItem onClick={handleThemeToggle(name)} key={name} value={name} aria-label={`Toggle ${name}`}>
                {icon}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <ToggleGroup type='multiple' variant='outline' value={[isSparkle ? 'sparkle' : '']}>
            <ToggleGroupItem onClick={handleSparkleToggle} key='sparkle' value='sparkle' aria-label='Toggle sparkle'>
              <Sparkles {...themeIconProps} />
            </ToggleGroupItem>
          </ToggleGroup>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className='text-xs font-normal text-muted-foreground'>Beta</DropdownMenuLabel>

        <DropdownMenuItem className='focus:bg-transparent justify-between'>
          Hotkeys
          <ToggleGroup
            onClick={onToggleAppHotkeys}
            type='single'
            variant='outline'
            value={isAppHotkeysEnabled ? 'appHotkeys' : ''}
          >
            <ToggleGroupItem key='appHotkeys' value='appHotkeys' aria-label='Toggle app hotkeys'>
              <KeyboardIcon {...iconProps.sm} />
            </ToggleGroupItem>
          </ToggleGroup>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleSignOut} className='justify-between cursor-pointer'>
          Sign out
          <LogOut {...themeIconProps} />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
})

TopNavUserMenu.displayName = 'TopNavUserMenu'
